import { jsx, jsxs } from "react/jsx-runtime";
import { useState, useRef, useEffect, useCallback } from "react";
import { parsePhoneNumber } from "awesome-phonenumber";
import { useOptionalConciergeContext } from "../../../ConciergeContextProvider.js";
import { toDigits, isPhoneValid, applyPhoneMask } from "./promptLoginUtils.js";
const PromptLoginWidget = ({
  data,
  onLoginCompleted,
  onLoginFailed,
  readOnly = false
}) => {
  var _a, _b;
  const ctx = useOptionalConciergeContext();
  const inlineSignIn = ctx == null ? void 0 : ctx.inlineSignIn;
  const inlineVerifyOtp = ctx == null ? void 0 : ctx.inlineVerifyOtp;
  const [step, setStep] = useState(data.stage === "otp" ? "otp" : "phone");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [session, setSession] = useState(null);
  const [error, setError] = useState(null);
  const [pendingEcho, setPendingEcho] = useState(null);
  const phoneInputRef = useRef(null);
  const otpInputRef = useRef(null);
  const lastAppliedRef = useRef(
    {}
  );
  useEffect(() => {
    var _a2, _b2;
    if (step === "phone") (_a2 = phoneInputRef.current) == null ? void 0 : _a2.focus();
    if (step === "otp") (_b2 = otpInputRef.current) == null ? void 0 : _b2.focus();
  }, [step]);
  const submitPhone = useCallback(
    async (rawPhone) => {
      var _a2;
      if (!inlineSignIn) {
        setError("Sign-in unavailable right now.");
        return;
      }
      setError(null);
      setPendingEcho(null);
      setStep("submitting");
      const e164 = ((_a2 = parsePhoneNumber(rawPhone, { regionCode: "US" }).number) == null ? void 0 : _a2.e164) ?? rawPhone;
      const res = await inlineSignIn(e164);
      if ("error" in res) {
        setStep("phone");
        setError(res.error);
        return;
      }
      setSession(res.session);
      setStep("otp");
    },
    [inlineSignIn]
  );
  const verifyOtp = useCallback(
    async (rawOtp) => {
      if (!inlineVerifyOtp) {
        setError("Sign-in unavailable right now.");
        onLoginFailed == null ? void 0 : onLoginFailed("inline verify unavailable");
        return;
      }
      setError(null);
      setPendingEcho(null);
      setStep("verifying");
      const res = await inlineVerifyOtp(session, rawOtp);
      if (res.success) {
        onLoginCompleted == null ? void 0 : onLoginCompleted();
      } else {
        setStep("otp");
        setError(res.error);
        onLoginFailed == null ? void 0 : onLoginFailed(res.error || "verification failed");
      }
    },
    [inlineVerifyOtp, session, onLoginCompleted, onLoginFailed]
  );
  useEffect(() => {
    var _a2;
    const incomingPhone = (_a2 = data.prefill) == null ? void 0 : _a2.phone;
    if (!incomingPhone) return;
    const digits = toDigits(incomingPhone).slice(0, 10);
    if (digits.length === 0) return;
    if (readOnly) {
      setPhone(digits);
      return;
    }
    const incomingConfirmed = data.confirmed === true;
    const alreadyApplied = lastAppliedRef.current.phone === digits && lastAppliedRef.current.phoneConfirmed === incomingConfirmed;
    if (alreadyApplied) return;
    setPhone(digits);
    if (incomingConfirmed) {
      lastAppliedRef.current.phone = digits;
      lastAppliedRef.current.phoneConfirmed = true;
      if (isPhoneValid(digits)) {
        void submitPhone(digits);
      } else {
        setError("That doesn't look like a complete number — please correct it.");
      }
      return;
    }
    if (step !== "phone") {
      setStep("phone");
      setOtp("");
      setSession(null);
      lastAppliedRef.current.otp = void 0;
      lastAppliedRef.current.otpConfirmed = void 0;
    }
    lastAppliedRef.current.phone = digits;
    lastAppliedRef.current.phoneConfirmed = false;
    setPendingEcho({ kind: "phone", value: digits });
    setError(null);
  }, [(_a = data.prefill) == null ? void 0 : _a.phone, data.confirmed, step, submitPhone]);
  useEffect(() => {
    var _a2;
    const incomingOtp = (_a2 = data.prefill) == null ? void 0 : _a2.otp;
    if (!incomingOtp) return;
    const digits = toDigits(incomingOtp).slice(0, 6);
    if (digits.length === 0) return;
    if (readOnly) {
      setOtp(digits);
      return;
    }
    const incomingConfirmed = data.confirmed === true;
    const alreadyApplied = lastAppliedRef.current.otp === digits && lastAppliedRef.current.otpConfirmed === incomingConfirmed;
    if (alreadyApplied) return;
    setOtp(digits);
    if (incomingConfirmed) {
      lastAppliedRef.current.otp = digits;
      lastAppliedRef.current.otpConfirmed = true;
      if (digits.length === 6) {
        void verifyOtp(digits);
      } else {
        setError("Only got part of the code — please say the full six digits again.");
      }
      return;
    }
    lastAppliedRef.current.otp = digits;
    lastAppliedRef.current.otpConfirmed = false;
    setPendingEcho({ kind: "otp", value: digits });
    setError(null);
  }, [(_b = data.prefill) == null ? void 0 : _b.otp, data.confirmed, step, verifyOtp]);
  useEffect(() => {
    if (pendingEcho && pendingEcho.kind === "phone" && pendingEcho.value !== phone) {
      setPendingEcho(null);
    }
  }, [phone, pendingEcho]);
  useEffect(() => {
    if (pendingEcho && pendingEcho.kind === "otp" && pendingEcho.value !== otp) {
      setPendingEcho(null);
    }
  }, [otp, pendingEcho]);
  if (!readOnly && (!inlineSignIn || !inlineVerifyOtp)) {
    return /* @__PURE__ */ jsx("div", { style: { ...CARD_STYLE, color: "var(--voice-widget-warn, #DC2626)" }, children: "Sign-in is unavailable in this environment." });
  }
  const isPhoneStep = step === "phone" || step === "submitting";
  const submitting = step === "submitting" || step === "verifying";
  const maskedPhone = applyPhoneMask(phone);
  const subtitle = isPhoneStep ? data.reason ? `Sign in to ${data.reason}. Just say your phone number — I'll fill it in for you.` : "Just say your phone number — I'll fill it in for you." : `We sent a 6-digit code to ${maskedPhone}. Say the digits aloud to continue.`;
  return /* @__PURE__ */ jsxs("div", { style: CARD_STYLE, role: "dialog", "aria-labelledby": "voice-login-title", children: [
    /* @__PURE__ */ jsxs("div", { style: { display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }, children: [
      /* @__PURE__ */ jsx("div", { style: ICON_STYLE, "aria-hidden": true, children: isPhoneStep ? PhoneGlyph : LockGlyph }),
      /* @__PURE__ */ jsxs("div", { children: [
        /* @__PURE__ */ jsx("div", { id: "voice-login-title", style: TITLE_STYLE, children: isPhoneStep ? "Sign in to continue" : "Confirm it's you" }),
        /* @__PURE__ */ jsx("div", { style: SUBTITLE_STYLE, children: subtitle })
      ] })
    ] }),
    /* @__PURE__ */ jsxs("label", { style: LABEL_STYLE, children: [
      isPhoneStep ? "Phone number" : "Verification code",
      /* @__PURE__ */ jsx(
        "input",
        {
          type: isPhoneStep ? "tel" : "text",
          inputMode: "none",
          maxLength: isPhoneStep ? 14 : 6,
          ref: isPhoneStep ? phoneInputRef : otpInputRef,
          value: isPhoneStep ? maskedPhone : otp,
          disabled: submitting,
          readOnly: true,
          "aria-readonly": true,
          tabIndex: -1,
          placeholder: isPhoneStep ? "(555) 555-5555" : "123456",
          style: INPUT_STYLE
        }
      )
    ] }),
    !readOnly && pendingEcho && /* @__PURE__ */ jsx("div", { style: CAPTION_STYLE, children: /* @__PURE__ */ jsxs("span", { children: [
      "Heard you say",
      " ",
      /* @__PURE__ */ jsx("strong", { style: { color: "var(--voice-widget-fg, #111827)" }, children: pendingEcho.kind === "phone" ? applyPhoneMask(pendingEcho.value) : pendingEcho.value }),
      ". Say ",
      /* @__PURE__ */ jsx("strong", { children: "yes" }),
      " to ",
      pendingEcho.kind === "phone" ? "send the code" : "verify",
      ", or correct it."
    ] }) }),
    !readOnly && error && /* @__PURE__ */ jsx("div", { style: ERROR_STYLE, children: error }),
    !readOnly && submitting && /* @__PURE__ */ jsx("div", { style: STATUS_STYLE, children: isPhoneStep ? "Sending code…" : "Verifying…" })
  ] });
};
const PhoneGlyph = /* @__PURE__ */ jsx("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2.2", strokeLinecap: "round", strokeLinejoin: "round", children: /* @__PURE__ */ jsx("path", { d: "M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7 2 2 0 0 1 1.72 2z" }) });
const LockGlyph = /* @__PURE__ */ jsxs("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2.2", strokeLinecap: "round", strokeLinejoin: "round", children: [
  /* @__PURE__ */ jsx("rect", { x: "3", y: "11", width: "18", height: "11", rx: "2" }),
  /* @__PURE__ */ jsx("path", { d: "M7 11V7a5 5 0 0 1 10 0v4" })
] });
const CARD_STYLE = {
  width: "100%",
  maxWidth: 380,
  background: "var(--voice-widget-card-bg, #FFFFFF)",
  border: "1px solid var(--voice-widget-border, #E5E7EB)",
  borderRadius: 16,
  padding: "18px 18px 16px",
  boxSizing: "border-box",
  boxShadow: "0 2px 12px rgba(0,0,0,0.07)"
};
const ICON_STYLE = {
  width: 34,
  height: 34,
  borderRadius: "50%",
  background: "var(--voice-widget-img-bg, #F9FAFB)",
  color: "var(--voice-widget-accent-dark, #0D9488)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0
};
const TITLE_STYLE = {
  fontSize: 14,
  fontWeight: 700,
  color: "var(--voice-widget-fg, #111827)",
  lineHeight: 1.3
};
const SUBTITLE_STYLE = {
  fontSize: 12,
  color: "var(--voice-widget-muted, #6B7280)",
  marginTop: 3,
  lineHeight: 1.4
};
const LABEL_STYLE = {
  display: "block",
  fontSize: 11,
  fontWeight: 600,
  color: "var(--voice-widget-muted, #6B7280)",
  textTransform: "uppercase",
  letterSpacing: 0.5,
  marginBottom: 12
};
const INPUT_STYLE = {
  display: "block",
  marginTop: 6,
  width: "100%",
  boxSizing: "border-box",
  padding: "12px 14px",
  fontSize: 16,
  fontWeight: 500,
  color: "var(--voice-widget-fg, #111827)",
  background: "var(--voice-widget-img-bg, #F9FAFB)",
  border: "1px solid var(--voice-widget-border, #E5E7EB)",
  borderRadius: 10,
  outline: "none",
  fontVariantNumeric: "tabular-nums",
  letterSpacing: 1
};
const CAPTION_STYLE = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 8,
  fontSize: 11,
  color: "var(--voice-widget-muted, #6B7280)",
  background: "var(--voice-widget-img-bg, #F9FAFB)",
  border: "1px dashed var(--voice-widget-border, #E5E7EB)",
  borderRadius: 8,
  padding: "6px 10px",
  marginBottom: 10
};
const ERROR_STYLE = {
  fontSize: 11,
  color: "var(--voice-widget-warn, #DC2626)",
  background: "var(--voice-widget-warn-bg, #FEF2F2)",
  border: "1px solid var(--voice-widget-warn-border, #FECACA)",
  borderRadius: 6,
  padding: "6px 10px",
  marginBottom: 10
};
const STATUS_STYLE = {
  width: "100%",
  textAlign: "center",
  padding: "11px 14px",
  fontSize: 12,
  fontWeight: 600,
  color: "var(--voice-widget-muted, #6B7280)"
};
export {
  PromptLoginWidget
};
//# sourceMappingURL=PromptLoginWidget.js.map
